<script>
import Layout from '@layouts/main'
import { merchantComputed, authComputed } from '@state/helpers'
import formatDate, { dateDiffInMinutes, formatLunar } from '@utils/format-date'
import { parseStringToDateTime } from '@utils/parse-string-to-date'
import _find from 'lodash/find'
import formatOrderStatus from '@utils/format-order-status'
import formatProductType from '@utils/format-product-type'
import formatOrderGoogleCalendarUrl from '@utils/format-order-google-calendar-url'
import MemberBanner from '@components/MemberBanner'

export default {
  page() {
    return {
      title: this.$t('global.MEMBER_ORDER'/* 我的結緣 */),
    }
  },
  components: { Layout, MemberBanner },
  filters: {
    formatDate,
    formatLunar,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isDeductProcessing: false,
      defaultImage: require('@assets/images/activity_default.png'),
    }
  },
  computed: {
    ...merchantComputed,
    ...authComputed,
    paramsSizeTotal() {
      let size = 0;

      this.order.params.forEach(param => {
        size += param.size * param.qty
      });

      return size
    },
    gender() {
      const gender = {
        male: this.$t('global.MALE' /* 先生 */),
        female: this.$t('global.FEMALE' /* 小姐 */),
      };

      return gender[this.order.gender]
    },
    paymentMethod() {
      const paymentMethod = {
        Prepaid: this.$t('global.PAYMENT_METHOD_PREPAID' /* 預付抵用 */),
        Cash: this.$t('global.PAYMENT_METHOD_CASH' /* 現金付款 */),
        Credit: this.$t('global.PAYMENT_METHOD_CREDIT' /* 信用卡付款 */),
        Free: this.$t('global.PAYMENT_METHOD_FREE' /* 免費 */),
        Vacc: this.$t('global.PAYMENT_METHOD_VACC' /* ATM虛擬帳戶 */),
        Linepay: this.$t('global.PAYMENT_METHOD_LINEPAY' /* Linepay */),
      };
      return paymentMethod[this.order.paymentMethod]
    },
    timeInterval() {
      const dateDiff = dateDiffInMinutes(`${this.order.startDate}`, `${this.order.endDate}`);

      if(dateDiff > 60 * 24 * 30) {
        return this.$tc('global.ABOUT_TIME_MOUTH', dateDiff / (60 * 24 * 30), { minute: Math.floor(dateDiff / (60 * 24 * 30)) }/* (預計{minute}個月) */)
      }else if(dateDiff > 60 * 24) {
        return this.$tc('global.ABOUT_TIME_DAY', dateDiff / (60 * 24), { minute: Math.floor(dateDiff / (60 * 24)) }/* (預計{minute}天) */)
      }else if(dateDiff > 60) {
        return this.$tc('global.ABOUT_TIME_HOUR', dateDiff / 60, { minute: Math.floor(dateDiff / 60) }/* (預計{minute}小時) */)
      }else {
        return this.$tc('global.ABOUT_TIME_MINUTE', dateDiff, { minute: Math.floor(dateDiff) }/* (預計{minute}分鐘) */)
      }
    },
    isNullOrder() {
      return typeof this.order === 'object' && Object.keys(this.order).length === 0
    }
  },
  methods: {
    formatOrderStatus,
    formatProductType,
    formatOrderGoogleCalendarUrl,
    bankDeadline() {
      return this.order.paymentExpiredDate ? formatDate( parseStringToDateTime(this.order.paymentExpiredDate), 'dateTime') : '';
    },
    bankCodeText(bankCode) {
      const bankCodeMap = {
        '812': '台新銀行',
        '004': '台灣銀行',
        '008': '華南銀行',
      };

      return bankCodeMap[bankCode]
    },
    handleCancel() {
      const vnodes = [];

      if (this.order.merchant.email) {
        vnodes.push(
        <a href={`mailto:${this.order.merchant.email}`}>
      <svg
        aria-hidden="true"
        focusable="false"
        data-prefix="far"
        data-icon="envelope"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
      class="svg-inline--fa fa-envelope fa-fw icon is-box fix-position is-left"
          >
          <path
        fill="currentColor"
        d="M464 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm0 48v40.805c-22.422 18.259-58.168 46.651-134.587 106.49-16.841 13.247-50.201 45.072-73.413 44.701-23.208.375-56.579-31.459-73.413-44.701C106.18 199.465 70.425 171.067 48 152.805V112h416zM48 400V214.398c22.914 18.251 55.409 43.862 104.938 82.646 21.857 17.205 60.134 55.186 103.062 54.955 42.717.231 80.509-37.199 103.053-54.947 49.528-38.783 82.032-64.401 104.947-82.653V400H48z"
          ></path>
          </svg>
        {this.order.merchant.email}
      </a>
      )
      }

      if (this.order.merchant.phoneNumber) {
        vnodes.push(<br />)
        vnodes.push(
        <a href={`tel:${this.order.merchant.phoneNumber}`}>
      <svg
        aria-hidden="true"
        focusable="false"
        data-prefix="fal"
        data-icon="mobile-android-alt"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 320 512"
      class="svg-inline--fa fa-mobile-android-alt fa-fw icon is-box fix-position is-left"
          >
          <path
        fill="currentColor"
        d="M224 96v240H96V96h128m48-96H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h224c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM48 480c-8.8 0-16-7.2-16-16V48c0-8.8 7.2-16 16-16h224c8.8 0 16 7.2 16 16v416c0 8.8-7.2 16-16 16H48zM244 64H76c-6.6 0-12 5.4-12 12v280c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12V76c0-6.6-5.4-12-12-12zm-48 352h-72c-6.6 0-12 5.4-12 12v8c0 6.6 5.4 12 12 12h72c6.6 0 12-5.4 12-12v-8c0-6.6-5.4-12-12-12z"
          ></path>
          </svg>
        {this.order.merchant.phoneNumber}
      </a>
      )
      }
      const createElement = this.$createElement;
      const messageElement = createElement('p', null, vnodes);

      this.$alert(
        messageElement,
        this.$t(
          'global.LIGHT_BOX_CANCEL_RESERVATION_TITLE',
          {
            actionName: this.$t(
              `product.${this.order.productType}.ORDER_CANCEL`
            ),
            merchantName: this.order.merchant.name,
          } /* 取消結緣請聯繫{merchantName} */
        ),
        {
          distinguishCancelAndClose: false,
          dangerouslyUseHTMLString: true,
          confirmButtonText: this.$t(
            'global.LIGHT_BOX_CANCEL_RESERVATION_CONFIRM' /* 我瞭解了 */
          ),
          confirmButtonClass: 'button is-primary is-rounded',
        }
      )
    },
    handleContact() {
      const vnodes = []

      if (this.order.merchant.email) {
        vnodes.push(
        <a href={`mailto:${this.order.merchant.email}`}>
      <svg
        aria-hidden="true"
        focusable="false"
        data-prefix="far"
        data-icon="envelope"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
      class="svg-inline--fa fa-envelope fa-fw icon is-box fix-position is-left"
          >
          <path
        fill="currentColor"
        d="M464 64H48C21.49 64 0 85.49 0 112v288c0 26.51 21.49 48 48 48h416c26.51 0 48-21.49 48-48V112c0-26.51-21.49-48-48-48zm0 48v40.805c-22.422 18.259-58.168 46.651-134.587 106.49-16.841 13.247-50.201 45.072-73.413 44.701-23.208.375-56.579-31.459-73.413-44.701C106.18 199.465 70.425 171.067 48 152.805V112h416zM48 400V214.398c22.914 18.251 55.409 43.862 104.938 82.646 21.857 17.205 60.134 55.186 103.062 54.955 42.717.231 80.509-37.199 103.053-54.947 49.528-38.783 82.032-64.401 104.947-82.653V400H48z"
          ></path>
          </svg>
        {this.order.merchant.email}
      </a>
      )
      }

      if (this.order.merchant.phoneNumber) {
        vnodes.push(<br />)
        vnodes.push(
        <a href={`tel:${this.order.merchant.phoneNumber}`}>
      <svg
        aria-hidden="true"
        focusable="false"
        data-prefix="fal"
        data-icon="mobile-android-alt"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 320 512"
      class="svg-inline--fa fa-mobile-android-alt fa-fw icon is-box fix-position is-left"
          >
          <path
        fill="currentColor"
        d="M224 96v240H96V96h128m48-96H48C21.5 0 0 21.5 0 48v416c0 26.5 21.5 48 48 48h224c26.5 0 48-21.5 48-48V48c0-26.5-21.5-48-48-48zM48 480c-8.8 0-16-7.2-16-16V48c0-8.8 7.2-16 16-16h224c8.8 0 16 7.2 16 16v416c0 8.8-7.2 16-16 16H48zM244 64H76c-6.6 0-12 5.4-12 12v280c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12V76c0-6.6-5.4-12-12-12zm-48 352h-72c-6.6 0-12 5.4-12 12v8c0 6.6 5.4 12 12 12h72c6.6 0 12-5.4 12-12v-8c0-6.6-5.4-12-12-12z"
          ></path>
          </svg>
        {this.order.merchant.phoneNumber}
      </a>
      )
      }
      const createElement = this.$createElement;
      const messageElement = createElement('p', null, vnodes);

      this.$alert(messageElement, this.$t('global.CONTACT_PREFIX', { name: this.order.merchant.name }/* 聯繫{name} */), {
        distinguishCancelAndClose: false,
        dangerouslyUseHTMLString: true,
        lockScroll: true,
        confirmButtonText: this.$t(
          'global.LIGHT_BOX_CANCEL_RESERVATION_CONFIRM' /* 我瞭解了 */
        ),
        confirmButtonClass: 'button is-primary is-rounded',
      })
    },
    orderIsCanPay(order) {
      const isExpired = parseStringToDateTime(order.paymentExpiredDate) <= new Date();

      return !order.paymentExpiredDate || !isExpired
    },
    handleToBookingDeductUnpaid(order) {
      this.isDeductProcessing = true;

      this.$store.dispatch('booking/fetchOrderDeduct', {
        orderKey: order.id
      })
        .then((response) => {
          window.globalData.order = {
            status: 'SUCCESS',
            type: 'deduct'
          };

          this.$router.push({
            name: 'order-detail',
            params: {
              merchantAlias: order.merchant.alias,
              orderKey: response.items.id
            },
            query: {
              language: this.$route.query.language,
            }
          })
        })
        .finally(() => {this.isDeductProcessing = false});
    },
    /**
     * 訂單票種
     * @return {Array}
     */
    orderParams() {
      let params = []

      if (this.order.params) {
        Object.keys(this.order.params).forEach((paramAlias) => {
          for (let i = 0; i < this.order.params[paramAlias].size; i++) {
            for (let j = 0; j < this.order.params[paramAlias].qty; j++) {
              params.push({
                ...this.order.params[paramAlias],
                sizeIndex: i + 1,
                qtyIndex: j + 1,
              })
            }
          }
        })
      }

      return params
    },
    getNameOrCompany(extrafields) {
      const fieldName = _find(Object.values(extrafields), (field) => { return field.type === 'name'; });
      const fieldCompany = _find(Object.values(extrafields), (field) => { return field.type === 'company'; });

      if(fieldName.value) return `姓名：${fieldName.value}`;
      else if(fieldCompany.value) return `公司行號：${fieldCompany.value}`;
      else return '姓名: 無'
    },
    hasNameOrCompany(extrafields) {
      const fieldName = _find(Object.values(extrafields), (field) => { return field.type === 'name'; });
      const fieldCompany = _find(Object.values(extrafields), (field) => { return field.type === 'company'; });

      if(fieldName.value) return 'name';
      else if(fieldCompany.value) return 'company';
      else return ''
    },
    formatLink(type, data = {}) {
      switch (type) {
        case 'member-order':
          return {
            name: 'member-order',
            params: {
              merchantAlias: this.order.merchant.alias,
            },
            query: {
              language: this.$route.query.language,
            }
          };
        case 'member-order-item':
          return {
            name: 'member-order-item',
            params: {
              merchantAlias: this.order.merchant.alias,
              orderKey: this.order.id,
            },
            query: {
              language: this.$route.query.language,
            }
          };
        case 'member-order-ticket':
          return {
            name: 'member-order-ticket',
            params: {
              merchantAlias: this.order.merchant.alias,
              orderKey: this.order.id,
            },
            query: {
              language: this.$route.query.language,
            },
          };
        case 'member-order-code':
          return {
            name: 'member-order-code',
            params: {
              merchantAlias: this.order.merchant.alias,
              orderKey: this.order.id,
            },
            query: {
              language: this.$route.query.language,
            },
          };
        case 'checkout-unpaid':
          return {
            name: 'checkout-unpaid',
            params: {
              merchantAlias: this.order.merchant.alias,
              productId: this.order.productAlias,
              orderKey: this.order.id,
            },
            query: {
              language: this.$route.query.language,
            },
          };
        case 'booking-product':
          return {
            name: 'booking-product',
            params: {
              merchantAlias: this.order.merchant.alias,
              productType: this.order.productType,
              productId: this.order.productAlias,
            },
            query: {
              language: this.$route.query.language,
            },
          };
        default:
          return '';
      }
    },
    formatText(type, data = {}) {
      switch (type) {
        case 'date-light':
        case 'date-worshipset':
          return formatLunar(data.order.startDate);
        case 'date-treasury':
        case 'date-pudu':
          return formatDate(data.order.startDate, 'dateSlash');
        case 'date-puja':
          const inventoryTitle = ` ${data.order.inventoryTitle}` || '';

          return `${formatDate(data.order.startDate, 'dateTime')}${inventoryTitle}`;
        default:
          return ''
      }
    },
    buttonList(type) {
      switch (type) {
        case 'paid':
          return [
            {
              textFormat: () => {
                if (this.order.productType === 'light') {
                  return this.$t('global.CHECK_LIGHT' /* 查看我的燈 */)
                }
                if (this.order.productType === 'worshipset') {
                  return this.$t('global.CHECK_WORSHIPSET' /* 查看我的斗燈 */)
                }
              },
              type: 'link',
              style: 'primary',
              if: this.formatProductType('category', this.order.productType) === 'light',
              to: this.formatLink('member-order-code'),
              disabled: this.formatOrderStatus('alias', this.order.status) !== 'complete',
              meta: {
                disabledTip: `(${this.$t('global.WAIT_ANFENG' /* 待廟方人員完成安奉作業後即可查看 */)})`,
              },
            },
            {
              text: this.$t('global.ADMISSION_QRCODE' /* 使用 QR Code */ ),
              type: 'link',
              style: 'primary',
              if: this.formatProductType('category', this.order.productType) === 'activity',
              to: this.formatLink('member-order-ticket'),
            },
            {
              text: this.$t('global.BACK_LIST'/* 回列表 */),
              type: 'link',
              style: 'primary-outline',
              to: this.formatLink('member-order'),
            },
            {
              text: this.$t('global.ADD_CALENDAR'/* 加入行事曆 */),
              type: 'a',
              style: 'black',
              if: this.formatProductType('category', this.order.productType) === 'activity',
              to: this.formatOrderGoogleCalendarUrl(this.order)
            },
            {
              text: this.$t(`product.${this.order.productType}.BUTTON_GO_TO_DETAIL`/* 查看資訊 */),
              type: 'link',
              style: 'black',
              to: this.formatLink('booking-product'),
            },
          ];
        case 'unpaid':
          return [
            {
              textFormat: () => {
                if(this.order.paymentMethod === 'Cash') {
                  return '無法線上付款（需使用現金付款）'
                }
                if (!this.orderIsCanPay(this.order)) {
                  return '無法線上付款（超過付款期限）'
                }
              },
              type: 'button',
              style: 'primary',
              if: !this.orderIsCanPay(this.order) || this.order.paymentMethod === 'Cash',
              disabled: true
            },
            {
              text: this.$t('global.PAYMENT_NOW' /* 前往付款 */),
              type: 'link',
              style: 'primary',
              if: this.orderIsCanPay(this.order) && !this.order.paymentMethod,
              to: this.formatLink('checkout-unpaid'),
            },
            {
              text: this.$t('global.BACK_LIST'/* 回列表 */),
              type: 'link',
              style: 'primary-outline',
              to: this.formatLink('member-order'),
            },
            {
              text: this.$t(`product.${this.order.productType}.BUTTON_GO_TO_DETAIL`/* 查看資訊 */),
              type: 'link',
              style: 'black',
              to: this.formatLink('booking-product'),
            },
          ];
        case 'cancel':
          return [
            {
              text: this.$t('global.BACK_LIST'/* 回列表 */),
              type: 'link',
              style: 'primary-outline',
              to: this.formatLink('member-order'),
            },
            {
              text: this.$t(
                `product.${this.order.productType}.BUTTON_GO_TO_DETAIL` /* 查看資訊 */
              ),
              type: 'link',
              style: 'black',
              to: this.formatLink('booking-product'),
            },
          ];
        default:
          return []
      }
    },
    formatExtrafieldTitle(type, title, extrafields) {
      switch (type) {
        case 'address':
          return '常駐地址';
        case 'phone':
          return '聯絡手機電話';
        case 'birth':
          const birth = () => {
            if(this.hasNameOrCompany(extrafields) === 'company') {
              return '創辦年份';
            }else {
              return '國曆生日';
            }
          };

          return birth();
        case 'dinco':
          return '闔家丁口數';
        default:
          return title;
      }
    }
  },
}
</script>
<template>
  <Layout>
    <div class="booking-container">
      <MemberBanner :member="currentMember"/>
      <template v-if="!isNullOrder">
        <div class="booking-section">
          <div class="level subsection is-tab-style">
            <div class="level-left booking-section__title">
              <router-link
                :to="formatLink('member-order')"
                class="is-link-black"
              >
                <BaseIcon name="angle-left" style="margin-right: 0.5rem"/>
                <span class="is-size-6">{{ $t('global.BACK_LIST'/*回列表頁*/) }}</span>
              </router-link>
            </div>
            <div class="level-right buttons is-tab-style">
              <router-link
                class="button is-tab-style"
                :class="{ 'is-active': $route.name === 'member-order-item' }"
                :to="formatLink('member-order-item')"
              >
                {{ $t('global.MEMBER_ORDER_DETAIL'/*結緣詳情*/) }}
              </router-link>
              <router-link
                v-if="formatOrderStatus('alias', order.status) === 'paid' && formatProductType('category', order.productType) === 'activity'"
                class="button is-tab-style"
                :class="{ 'is-active': $route.name === 'member-order-ticket' }"
                :to="formatLink('member-order-ticket')"
              >
                {{ $t('global.QRCODE'/*QR Codes*/) }}
              </router-link>
              <router-link
                v-if="formatOrderStatus('alias', order.status) === 'complete' && formatProductType('category', order.productType) === 'light'"
                class="button is-tab-style"
                :class="{ 'is-active': $route.name === 'member-order-code' }"
                :to="formatLink('member-order-code')"
              >
                {{ $t('global.LIGHT_DETAIL'/*點燈明細*/) }}
              </router-link>
            </div>
          </div>
          <div class="subsection product-wrapper">
            <div :class="$style.wrapper">
              <div :class="$style.inner" class="is-mobile-column-reverse">
                <div class="columns is-multiline is-mobile is-mobile-column-reverse">
                  <div class="column is-full-mobile is-three-fifths-tablet">
                    <div class="media is-align-items-stretch">
                      <figure class="media-left">
                        <picture>
                          <BaseImage
                            :src="order.introimage"
                            :error-src="defaultImage"
                            :class="$style.image"
                            class-name="is-cover"
                          />
                        </picture>
                      </figure>
                      <div class="media-content">
                        <div :class="$style.titleBox" class="level" style="margin-bottom: 0.5rem">
                          <div :class="$style.title" class="level-left">
                            <b-tag
                              :type="`is-status-${formatOrderStatus('alias', order.status)}`"
                              rounded
                              style="margin-right: 0.5rem"
                            >
                              {{  formatOrderStatus('text', order.status, order.paymentMethod) }}
                            </b-tag> {{ order.productName }}
                          </div>
                        </div>
                        <div class="has-text-grey is-text-content" :class="$style.orderMeta">
                          {{ order.merchant.name }}
                          <br>{{ $t(`product.${order.productType}.TITLE_TIME`/*活動時間*/) }}：{{formatText(`date-${order.productType}`, { order: order })}}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="column is-full-mobile is-two-fifths-tablet">
                    <div>
                      <div class="level" style="margin-bottom: 0" :class="$style.orderSerial">
                        <div class="level-left"></div>
                        <div class="level-right">
                          <div
                            :class="$style['order-number-text']"
                            class="has-text-grey">
                            {{ $t('global.ORDER_NO'/*結緣編號*/) }} #{{order.orderNo}}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div :class="$style.inner">
                <div class="columns is-multiline is-mobile">
                  <div class="column is-one-fifth-tablet is-full-mobile" :class="$style.orderItem">
                    {{ $t('global.ORDER_DETAIL' /* 結緣明細 */) }}
                  </div>
                  <template>
                    <div v-if="order.prepaid" class="column">
                      <div v-for="(param, index) in order.params" :key="index" class="level is-mobile" style="margin-bottom: 0.5rem;">
                        <div class="level-left">
                          {{param.name}} x {{param.qty}}
                        </div>
                        <div class="level-right">
                      <span class="has-text-right">
                        {{ $t('global.TOTAL_DEDUCT', { size: param.qty } /* 抵用 {size} 次 */) }}
                      </span>
                        </div>
                      </div>
                      <hr :class="$style.hr">
                      <div class="level is-mobile" style="margin-bottom: 0.5rem">
                        <div class="level-left">
                          {{ $t('global.USE_PREPAID'/*使用方案*/) }}
                        </div>
                        <div class="level-right has-text-primary">
                          <router-link :to="{ name: 'member-prepaid-item', params: { orderKey: order.prepaid.id }, query: { language: $route.query.language }}">
                            {{order.prepaid.title}}
                          </router-link>
                        </div>
                      </div>
                      <hr :class="$style.hr">
                      <div class="level is-mobile" >
                        <div class="level-left">
                          {{ $t('global.TOTAL'/*總計*/) }}
                        </div>
                        <div class="level-right">
                      <span class="has-text-right is-size-6">
                        {{ $t('global.TOTAL_DEDUCT', { size: order.prepaid.deductTimes } /* 抵用 {size} 次 */) }}
                      </span>
                        </div>
                      </div>
                    </div>
                    <div v-else class="column">
                      <div v-for="(param, index) in order.params" :key="index" class="level is-mobile" style="margin-bottom: 0.5rem;">
                        <div class="level-left">
                          {{param.name}} x {{param.qty}}
                        </div>
                        <div class="level-right">
                          <span class="has-text-right has-text-grey" style="min-width: 80px;">
                              ${{param.total}}
                            </span>
                        </div>
                      </div>
                      <hr :class="$style.hr">
                      <div class="level is-mobile" style="margin-bottom: 0.5rem">
                        <div class="level-left">
                          {{ $t('global.SUBTOTAL'/*小計*/) }}
                        </div>
                        <div class="level-right">
                          ${{order.priceBase}}
                        </div>
                      </div>
                      <div v-if="order.couponTitle && order.couponCode" class="level is-mobile" >
                        <div class="level-left">
                          {{ $t('global.COUPON'/*優惠券*/) }} ({{order.couponTitle}}：{{order.couponCode}})
                        </div>
                        <div class="level-right has-text-grey">
                          ${{order.priceTotal - order.priceBase}}
                        </div>
                      </div>
                      <hr :class="$style.hr">
                      <div class="level is-mobile" >
                        <div class="level-left">
                          {{ $t('global.TOTAL'/*總計*/) }}
                        </div>
                        <div class="level-right">
                          <!--<span>
                            {{paramsSizeTotal}} <BaseIcon name="ticket-alt" />
                          </span>-->
                          <span class="has-text-right is-size-6" style="min-width: 80px;">
                        ${{order.priceTotal}}
                      </span>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
              <div v-if="order.paymentMethod === 'Vacc'" :class="$style.inner">
                <div class="columns is-multiline is-mobile">
                  <div class="column is-one-fifth-tablet is-full-mobile" :class="$style.orderItem">
                    {{ $t('global.TRANSFER_INFORS'/*轉帳資訊*/) }}
                  </div>
                  <div class="column is-text-content">
                    <div>
                      {{ $t('global.TRANSFER_BANK'/*轉帳銀行*/) }} ｜ {{ bankCodeText(order.BankCode) }} {{ order.BankCode }}
                    </div>
                    <div>
                      {{ $t('global.TRANSFER_ACCOUNT'/*轉帳帳號*/) }} ｜ {{ order.VACCNo }}
                    </div>
                    <div v-if="bankDeadline">
                      {{ $t('global.TRANSFER_DEADLINE'/*轉帳期限*/) }} ｜ {{ bankDeadline() }}
                    </div>
                    <div>
                      {{ $t('global.TRANSFER_AMOUNT'/*轉帳金額*/) }} ｜ ${{ order.priceTotal }}元
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="order.customerInfos && order.customerInfos.length > 0" :class="$style.inner">
                <div class="columns is-multiline is-mobile">
                  <div class="column is-one-fifth-tablet is-full-mobile" :class="$style.orderItem">
                    {{ $t('global.RESERVATION_INFORS'/*登記資訊*/) }}
                  </div>
                  <div class="column is-text-content has-text-grey">
                    <div class="collapses">
                      <template v-for="(param, index) in orderParams()">
                        <b-collapse
                          v-if="order.customerInfos[index]"
                          :key="index"
                          class="style-order"
                          :open="false"
                          :class="$style.ticket"
                        >
                          <div slot="trigger"
                               slot-scope="props"
                               class="card-header"
                               :class="[$style.ticketHeader, { 'is-open': props.open }]"
                               role="button"
                          >
                            <div :class="$style['ticketHeader__title']" class="card-header-title">
                              <div :class="$style['ticketHeader__name']">
                                {{ param.name }}
                                <div :class="$style['ticketHeader__subname']">
                                  {{getNameOrCompany(order.customerInfos[index].bySizeExtrafields)}}
                                </div>
                              </div>
                            </div>
                            <div :class="$style['ticketHeader__icon']" class="card-header-icon">
                              <span :class="$style.switchText" v-text="props.open ? $t('global.HIDE'/* 收起 */) : $t('global.OPEN'/* 展開 */)" ></span>
                              <span class="icon">
                               <BaseIcon :name="props.open ? 'angle-up' : 'angle-down'" />
                             </span>
                            </div>
                          </div>
                          <div>
                            <template v-for="(value, key) in order.customerInfos[index].bySizeExtrafields">
                              <div
                                v-if="!value.adminEdit && value.type !== 'name' && value.type !== 'company'"
                                :key="key"
                                :class="$style['each-card-content']"
                              >
                                <div :class="$style['each-card-content-title']">{{ formatExtrafieldTitle(value.type, value.title, order.customerInfos[index].bySizeExtrafields) }}</div>
                                <div :class="$style['each-card-content-value']">{{value.value || "無"}}</div>
                              </div>
                            </template>
                          </div>
                        </b-collapse>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              <div :class="$style.inner">
                <div class="columns is-multiline is-mobile">
                  <div class="column is-one-fifth-tablet is-full-mobile" :class="$style.orderItem">
                    {{ $t('global.ORDER_INFO'/*結緣資訊*/) }}
                  </div>
                  <div class="column is-text-content">
                    <div>
                      {{ $t('global.ORDER_USE_INFO_NAME'/*使用人姓名*/) }}：{{order.fullName}} {{gender}}
                    </div>
                    <div>
                      {{ $t('global.ORDER_USE_INFO_PHONE'/*使用人電話*/) }}：{{order.phoneCode}} {{order.phoneNumber}}
                    </div>
                    <div>
                      {{ $t('global.ORDER_USE_INFO_EMAIL'/*使用人Email*/) }}：{{order.email}}
                    </div>
                    <div>
                      {{ $t('global.ORDER_USE_INFO_NOTE'/*其他備註*/) }}：<span v-if="order.note">{{order.note}}</span><span v-else>{{ $t('global.ORDER_USE_INFO_NOTE_NULL'/*無*/) }}</span>
                    </div>
                    <div v-if="order.paymentMethod">
                      <span>{{ $t('global.PAYMENT_METHOD'/*付款方式*/) }}：</span>
                      <span>{{paymentMethod}}</span>
                    </div>
                    <div v-if="order.created">
                      <span>{{ $t('global.ORDER_CREATED'/*成立時間*/) }}：</span>
                      <span>{{order.created | formatDate('dateTimeWeekDay')}}</span>
                    </div>
                    <div v-if="order.paidDate">
                      <span>{{ $t('global.ORDER_PAID'/*完成時間*/) }}：</span>
                      <span>{{order.paidDate | formatDate('dateTimeWeekDay')}}</span>
                    </div>
                    <div v-if="order.cancelDate">
                      <span>{{ $t('global.ORDER_CANCEL'/*取消時間*/) }}：</span>
                      <span>{{order.cancelDate | formatDate('dateTimeWeekDay')}}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="subsection">
            <template v-if="formatOrderStatus('alias', order.status) === 'unpaid'">
              <BaseButtons :buttons="buttonList('unpaid')" />
            </template>
            <template v-if="formatOrderStatus('paid', order.status)">
              <BaseButtons :buttons="buttonList('paid')" />
            </template>
            <div v-if="formatOrderStatus('alias', order.status) === 'cancel'">
              <BaseButtons :buttons="buttonList('cancel')" />
            </div>
          </div>
        </div>
        <div class="booking-section--outside">
          <b-button
            type="is-black"
            :class="$style.connactStore"
            rounded
            outlined
            class="is-fullwidth"
            @click="handleContact"
          >{{ $t('global.CONTACT_MERCHANT'/* 聯絡宮廟 */) }}</b-button>
        </div>
      </template>
      <template v-else>
        <div class="defaultBox">
          <img alt="merchant default" class="defaultBox__image" src="@assets/images/information_default.png" />
          <h3 class="defaultBox__text">
            {{$t('global.NOT_FOUND_PREFIX', { text: $t('global.ORDER') } /*找不到結緣紀錄*/)}}
          </h3>
        </div>
        <router-link
          class="button is-primary is-rounded is-fullwidth"
          :to="formatLink('member-order')"
        >
          {{ $t('global.BACK_LIST'/*回列表*/) }}
        </router-link>
      </template>
    </div>
  </Layout>
</template>
<style lang="scss" module>
  .wrapper {
    &:not(:last-child) {
      .inner {
        border-bottom: 1px solid $color-border-base;
      }
    }
    .inner:not(:last-child) {
      border-bottom: 1px solid $color-border-base;
    }

    .inner {
      padding: 1rem 0 0.9rem;
      margin: 0 1.5rem;
    }
  }

  .titleBox {
    align-items: flex-start;
    margin-bottom: 1rem;
    .title {
      align-items: flex-start;
    }
  }

  .image {
    position: relative;
    width: 150px;
    height: 100px;
    overflow: hidden;
    background: $grey-lighter no-repeat;
    background-size: cover;
    border-radius: $size-image-border-radius;

    @include mobile {
      width: 100px;
      height: 70px;
    }
  }
  .hr {
    margin: 1rem 0;
    background: transparent;
    border-bottom: 1px dashed $color-border-base;
  }
  .order-number-text{
    font-size: 15px;
    color: $color-text-order !important;
    @include mobile {
      font-size: 13px;
    }
  }
  .orderMeta {
    font-size: 15px;
    line-height: 1.75;
    @include mobile {
      font-size: 13px;
      line-height: 1.4;
    }
  }
  .orderItem{
    color: $color-primary;
  }
  .switchText{
    color: $color-heading-text;
  }
  .ticketHeader__name{
    font-weight: normal;
    color: $color-text-about;
  }
  .ticketHeader__subname{
    font-size: 13px;
    color: grey;
  }
  .ticketHeader{
    // margin: 0 1.5rem;
    .ticketHeader__title,.ticketHeader__icon{
      padding: 0.75rem 0;
    }
    .ticketHeader__icon{
      &:hover span{
        color: $color-nav;
      }
    }
  }
  .each-card-content{
    margin-bottom: 10px;
    font-size: 15px;
    line-height: 28px;
    letter-spacing: 1px;
    &-title{
      color: $color-text-title;
    }
    &-value{
      color: $color-text-about;
    }
  }
  .orderSection{
    padding: 0 1.5rem !important;
  }
  .order-infors-content{
    font-size: 15px;
    line-height: 28px;
    color: $color-text-about !important;
    letter-spacing: 1px;
  }
  .backList{
    border-color: $color-border-back !important;
  }
  .option{
    font-weight: normal !important;
    border-color: $color-border-option !important;
  }
  .orderSerial{
    @include touch {
      margin-left: -14px;
    }
  }
</style>
